<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    useMatterContextModalStore,
    isEntityFieldModified,
    isEntityModified,
    isEntityQueuedForPublish,
    type EntityRow,
    type EntityTrackedField,
} from '../../stores/matterContextModalStore';
import MatterContextEditableField from './MatterContextEditableField.vue';
import { SHOW_ENTITY_NAME_PARTS } from '../../config/featureFlags';

const { t } = useI18n();

const ENTITY_TYPES = ['Person', 'Organization', 'Group', 'Department', 'Place'];

// Mirrors the HTML5 email input pattern — good enough for a curation UI.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const validateEmail = (value: string): string | null => {
    if (!value) return null; // empty is allowed (field is optional)
    return EMAIL_RE.test(value) ? null : t('matterContext.entityDialog.invalidEmail');
};
// Name is required to create an entity (the backend rejects a nameless
// create with a 422). Enforced on the field so the same validation that
// gates the edit path also gates the add path.
const validateName = (value: string): string | null =>
    value.trim() ? null : t('matterContext.entityDialog.nameRequired');

const props = defineProps<{
    visible: boolean;
    currentId: string | null;
    // When true the dialog edits a fresh, not-yet-persisted entity held
    // locally (record nav hidden, footer shows "Add entity"); the row is
    // only created in the store when the user confirms. Mirrors the add
    // row it replaces, but with the full edit-field feature set.
    createNew?: boolean;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'update:currentId', id: string): void;
}>();

const store = useMatterContextModalStore();

// Local draft backing create mode. Reset each time the dialog (re)opens in
// create mode so a prior cancelled add never leaks into the next one.
function blankEntityDraft(): EntityRow {
    const base = {
        entityType: 'Person',
        name: '',
        context: '',
        firstName: '',
        lastName: '',
        email: '',
    };
    return { id: '__new-entity__', slug: '', ...base, original: { ...base } };
}
const draftRow = ref<EntityRow>(blankEntityDraft());
watch(
    () => props.visible && !!props.createNew,
    (active) => {
        if (active) draftRow.value = blankEntityDraft();
    },
);

const index = computed(() => store.entities.findIndex((row) => row.id === props.currentId));
// Unified accessor: the local draft in create mode, the matched store row
// otherwise. Every field/handler below reads through this.
const entity = computed(() =>
    props.createNew ? draftRow.value : index.value >= 0 ? store.entities[index.value] : null,
);
const total = computed(() => store.entities.length);

const displayName = computed(() => {
    const e = entity.value;
    if (!e) return '';
    if (SHOW_ENTITY_NAME_PARTS) {
        const full = [e.firstName, e.lastName].filter(Boolean).join(' ').trim();
        if (full) return full;
    }
    if (props.createNew) return e.name || t('matterContext.entityDialog.newEntity');
    return e.name || t('matterContext.entityDialog.untitledEntity');
});

const atFirst = computed(() => index.value <= 0);
const atLast = computed(() => index.value < 0 || index.value >= total.value - 1);

// Edits write to the local draft in create mode, the store row otherwise.
const update = (patch: Parameters<typeof store.updateEntity>[1]) => {
    if (!entity.value) return;
    if (props.createNew) Object.assign(draftRow.value, patch);
    else store.updateEntity(entity.value.id, patch);
};

// No baseline-diff styling for a brand-new entity (everything is "new",
// not "modified").
const fieldModified = (field: EntityTrackedField): boolean =>
    !props.createNew && entity.value ? isEntityFieldModified(entity.value, field) : false;

const entityModified = computed(() =>
    !props.createNew && entity.value ? isEntityModified(entity.value) : false,
);

// Person-only fields (first/last/email) are shown only for individuals; other
// entity types expose just Name, Context, and Type.
const isIndividual = computed(() => entity.value?.entityType === 'Person');

const resetEntity = () => {
    if (entity.value) store.resetEntity(entity.value.id);
};

// Status switch (edit mode only): Active = included, Inactive = excluded
// (soft-deleted). Mirrors the row switch and reuses the same store actions.
// Blur after toggling so the styled tooltip doesn't stick open.
const toggleStatus = (e: Event) => {
    const row = entity.value;
    if (!row) return;
    if (row.deleted) store.restoreEntity(row.id);
    else store.deleteEntity(row.id);
    (e.target as HTMLElement | null)?.blur();
};

// Offer to split the Name into first/last for an individual whose name looks
// like a 2–3 word person name and whose first/last fields are still empty.
// Leading honorifics (e.g. "Dr.") are trimmed before counting and splitting.
const NAME_PREFIXES = new Set([
    'dr',
    'mr',
    'mrs',
    'ms',
    'miss',
    'mx',
    'prof',
    'rev',
    'hon',
    'sir',
    'dame',
    'capt',
    'col',
    'gen',
    'lt',
    'sgt',
    'maj',
    'fr',
]);
const nameWords = computed(() => {
    const words = (entity.value?.name ?? '').trim().split(/\s+/).filter(Boolean);
    while (words.length && NAME_PREFIXES.has(words[0].replace(/\.$/, '').toLowerCase())) {
        words.shift();
    }
    return words;
});
const canSplitName = computed(() => {
    if (!SHOW_ENTITY_NAME_PARTS) return false;
    const e = entity.value;
    if (!e || e.entityType !== 'Person') return false;
    if (e.firstName.trim() || e.lastName.trim()) return false;
    return nameWords.value.length >= 2 && nameWords.value.length <= 3;
});
const splitNameIntoParts = () => {
    const e = entity.value;
    if (!e || nameWords.value.length < 2) return;
    update({
        firstName: nameWords.value[0],
        lastName: nameWords.value.slice(1).join(' '),
    });
};

// Refs to every editable field so we can detect / commit / discard
// in-progress edits when the user tries to navigate or close. The body is
// keyed by the entity id in the template, so switching records remounts the
// fields in view mode (no stale edit state carries across).
type FieldApi = InstanceType<typeof MatterContextEditableField>;
const nameField = ref<FieldApi | null>(null);
const contextField = ref<FieldApi | null>(null);
const firstNameField = ref<FieldApi | null>(null);
const lastNameField = ref<FieldApi | null>(null);
const typeField = ref<FieldApi | null>(null);
const emailField = ref<FieldApi | null>(null);

const allFields = (): FieldApi[] =>
    [nameField, contextField, firstNameField, lastNameField, typeField, emailField]
        .map((r) => r.value)
        .filter((f): f is FieldApi => f !== null);

const anyDirty = () => allFields().some((f) => f.isDirty());
const saveAll = (): boolean => {
    let ok = true;
    for (const f of allFields()) if (!f.commit()) ok = false;
    return ok;
};

// There is no per-entity Save: committed edits (and added rows) are QUEUED
// in the store and staged + published together when the user publishes the
// matter context. The footer surfaces that queued state for the current row.
// Mirrors EventProfileDialog.
const entityQueued = computed(() =>
    !props.createNew && entity.value ? isEntityQueuedForPublish(entity.value) : false,
);

// Create mode: the Add button enables once a (committed) name is present —
// name commits on ✓/Enter/blur, like every other field. Commits all
// in-progress edits (running their validators, e.g. email) then adds the
// row to the store, where it sits queued until publish. Mirrors the events
// dialog.
const canCreate = computed(() => props.createNew && draftRow.value.name.trim().length > 0);
const createEntity = () => {
    if (!saveAll()) return; // a field failed validation (e.g. email)
    const d = draftRow.value;
    if (!d.name.trim()) return;
    store.addEntity(d.entityType, d.name, d.context, d.email);
    store.showToast(t('matterContext.entityDialog.addedToast'));
    emit('close');
};

// Pending navigation/close intent, held while the unsaved-changes warning
// is shown.
type PendingAction = { type: 'navigate'; targetId: string } | { type: 'close' };
const pendingAction = ref<PendingAction | null>(null);

const goTo = (target: number) => {
    if (target < 0 || target >= total.value) return;
    const targetId = store.entities[target].id;
    // Only an in-progress field edit blocks leaving — committed edits are
    // queued in the store and survive navigation.
    if (anyDirty()) {
        pendingAction.value = { type: 'navigate', targetId };
    } else {
        emit('update:currentId', targetId);
    }
};

const requestClose = () => {
    // Create mode: closing cancels the add outright — the draft is local and
    // unsaved, so there's nothing to warn about. The watch resets it on the
    // next open.
    if (props.createNew) {
        emit('close');
        return;
    }
    if (anyDirty()) {
        pendingAction.value = { type: 'close' };
    } else {
        emit('close');
    }
};

const performPending = () => {
    const action = pendingAction.value;
    pendingAction.value = null;
    if (!action) return;
    if (action.type === 'navigate') emit('update:currentId', action.targetId);
    else emit('close');
};

const onWarnCancel = () => {
    pendingAction.value = null;
};
// Abandon the in-progress field edit; committed (queued) changes are kept.
const onWarnDiscard = () => {
    performPending();
};
// Commit the in-progress field edits to the store (queueing them). If a
// field fails validation, stay put with the error surfaced so the user can
// fix it rather than silently losing the change.
const onWarnSave = () => {
    if (saveAll()) performPending();
    else pendingAction.value = null;
};
</script>

<template>
    <div v-if="visible && entity" class="mc-profile-backdrop" @click.self="requestClose">
        <aside class="mc-profile" role="dialog" :aria-label="t('matterContext.entityDialog.ariaLabel')">
            <header class="mc-profile-header">
                <span class="mc-profile-eyebrow">{{
                    createNew ? t('matterContext.entityDialog.newEntity') : t('matterContext.entityDialog.eyebrow')
                }}</span>
                <button type="button" class="mc-profile-close" :title="t('common.close')" @click="requestClose">
                    <span class="material-icons" style="font-size: 18px">close</span>
                </button>
            </header>

            <div class="mc-profile-subheader">
                <h2 class="mc-profile-title">{{ displayName }}</h2>
                <div v-if="!createNew" class="mc-profile-nav">
                    <span class="mc-profile-record">
                        {{ t('matterContext.common.recordLabel') }} <strong>{{ index + 1 }}</strong> /{{ total }}
                    </span>
                    <button
                        type="button"
                        class="mc-profile-navbtn"
                        :title="t('matterContext.common.first')"
                        :disabled="atFirst"
                        @click="goTo(0)"
                    >
                        <span class="material-icons" style="font-size: 18px">first_page</span>
                    </button>
                    <button
                        type="button"
                        class="mc-profile-navbtn"
                        :title="t('matterContext.common.previous')"
                        :disabled="atFirst"
                        @click="goTo(index - 1)"
                    >
                        <span class="material-icons" style="font-size: 18px">chevron_left</span>
                    </button>
                    <button
                        type="button"
                        class="mc-profile-navbtn"
                        :title="t('matterContext.common.next')"
                        :disabled="atLast"
                        @click="goTo(index + 1)"
                    >
                        <span class="material-icons" style="font-size: 18px">chevron_right</span>
                    </button>
                    <button
                        type="button"
                        class="mc-profile-navbtn"
                        :title="t('matterContext.common.last')"
                        :disabled="atLast"
                        @click="goTo(total - 1)"
                    >
                        <span class="material-icons" style="font-size: 18px">last_page</span>
                    </button>
                </div>
            </div>

            <div :key="entity.id" class="mc-profile-body">
                <section class="mc-profile-card">
                    <div class="mc-profile-card-head">
                        <h3 class="mc-profile-card-title">{{ t('matterContext.entityDialog.aboutCard') }}</h3>
                        <button
                            v-if="canSplitName"
                            type="button"
                            class="mc-split-name-btn"
                            :title="t('matterContext.entityDialog.splitNameTitle', { name: entity.name })"
                            @click="splitNameIntoParts"
                        >
                            <span class="material-icons" style="font-size: 16px">call_split</span>
                            <span>{{ t('matterContext.entityDialog.splitName') }}</span>
                        </button>
                    </div>
                    <div v-if="!createNew" class="mc-field mc-field-status">
                        <div class="mc-field-label">{{ t('matterContext.common.status') }}</div>
                        <div class="mc-field-value">
                            <label class="mc-switch-wrap mc-status-switch">
                                <span class="mc-switch">
                                    <input
                                        type="checkbox"
                                        class="mc-switch-input"
                                        :checked="!entity.deleted"
                                        @change="toggleStatus($event)"
                                    />
                                    <span class="mc-switch-track">
                                        <span class="mc-switch-thumb"></span>
                                    </span>
                                </span>
                                <span
                                    class="mc-status-label"
                                    :class="{ 'mc-status-label-active': !entity.deleted }"
                                    >{{ entity.deleted ? t('matterContext.common.inactive') : t('matterContext.common.active') }}</span
                                >
                                <span
                                    class="mc-switch-tooltip mc-switch-tooltip-below"
                                    role="tooltip"
                                >
                                    <!-- eslint-disable-next-line vue/no-v-html -- static i18n string with a bolded action word, no user data -->
                                    <span v-html="t(entity.deleted ? 'matterContext.entities.tooltipExclude' : 'matterContext.entities.tooltipInclude')"></span>
                                </span>
                            </label>
                        </div>
                    </div>
                    <MatterContextEditableField
                        ref="typeField"
                        :label="t('matterContext.entities.colType')"
                        :value="entity.entityType"
                        :modified="fieldModified('entityType')"
                        edit-type="select"
                        :options="ENTITY_TYPES"
                        @save="(v) => update({ entityType: v })"
                    />
                    <MatterContextEditableField
                        ref="nameField"
                        :label="t('matterContext.entities.colName')"
                        :value="entity.name"
                        :modified="fieldModified('name')"
                        :placeholder="t('matterContext.entityDialog.addName')"
                        :validate="validateName"
                        @save="(v) => update({ name: v })"
                    />
                    <MatterContextEditableField
                        v-if="isIndividual && SHOW_ENTITY_NAME_PARTS"
                        ref="firstNameField"
                        :label="t('matterContext.entities.firstName')"
                        :value="entity.firstName"
                        :modified="fieldModified('firstName')"
                        :placeholder="t('matterContext.entityDialog.addFirstName')"
                        @save="(v) => update({ firstName: v })"
                    />
                    <MatterContextEditableField
                        v-if="isIndividual && SHOW_ENTITY_NAME_PARTS"
                        ref="lastNameField"
                        :label="t('matterContext.entities.lastName')"
                        :value="entity.lastName"
                        :modified="fieldModified('lastName')"
                        :placeholder="t('matterContext.entityDialog.addLastName')"
                        @save="(v) => update({ lastName: v })"
                    />
                    <MatterContextEditableField
                        v-if="isIndividual"
                        ref="emailField"
                        :label="t('matterContext.entities.email')"
                        :value="entity.email"
                        :modified="fieldModified('email')"
                        :placeholder="t('matterContext.entityDialog.addEmail')"
                        :validate="validateEmail"
                        @save="(v) => update({ email: v })"
                    />
                    <MatterContextEditableField
                        ref="contextField"
                        :label="t('matterContext.entities.colContext')"
                        :value="entity.context"
                        :modified="fieldModified('context')"
                        edit-type="textarea"
                        :placeholder="t('matterContext.common.addContext')"
                        @save="(v) => update({ context: v })"
                    />
                </section>
            </div>

            <footer v-if="createNew" class="mc-profile-footer">
                <button
                    type="button"
                    class="mc-profile-save"
                    :disabled="!canCreate"
                    @click="createEntity"
                >
                    {{ t('matterContext.entityDialog.addEntityButton') }}
                </button>
            </footer>
            <footer v-else-if="entityQueued" class="mc-profile-footer">
                <button
                    v-if="entityModified"
                    type="button"
                    class="mc-profile-reset"
                    @click="resetEntity"
                >
                    <span class="material-icons" style="font-size: 16px">restart_alt</span>
                    {{ t('matterContext.common.resetChanges') }}
                </button>
                <span class="mc-profile-queued-note">
                    <span class="mc-row-mod-dot" aria-hidden="true"></span>
                    {{ t('matterContext.common.changesInDraft') }}
                    <span class="mc-draft-tooltip" role="tooltip">
                        {{ t('matterContext.common.draftTooltip') }}
                    </span>
                </span>
            </footer>

            <div v-if="pendingAction" class="mc-confirm-backdrop" @click.self="onWarnCancel">
                <div class="mc-confirm" role="alertdialog" :aria-label="t('matterContext.common.unsavedChangesTitle')">
                    <h3 class="mc-confirm-title">{{ t('matterContext.common.unsavedChangesTitle') }}</h3>
                    <p class="mc-confirm-body">
                        {{ t('matterContext.common.unsavedChangesBody') }}
                    </p>
                    <div class="mc-confirm-actions">
                        <button type="button" class="mc-confirm-btn" @click="onWarnCancel">
                            {{ t('common.cancel') }}
                        </button>
                        <button
                            type="button"
                            class="mc-confirm-btn mc-confirm-btn-danger"
                            @click="onWarnDiscard"
                        >
                            {{ t('matterContext.common.discardChanges') }}
                        </button>
                        <button
                            type="button"
                            class="mc-confirm-btn mc-confirm-btn-primary"
                            @click="onWarnSave"
                        >
                            {{ t('matterContext.common.saveChanges') }}
                        </button>
                    </div>
                </div>
            </div>
        </aside>
    </div>
</template>
